import { html, TemplateResult, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_SANDBOX_ICAP_SENSOR } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  IcapModuleFormValue,
  SandboxIcapSensorSearchableKeywords,
} from '../../../../utils/uiSettings/sandbox/sandbox.types';
import {
  getSandboxIcapSensorSearchableKeywords,
  ICAP_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/sandbox/sandbox.values';
import {
  ManualSubmissionApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzLocalSandboxInstanceWebComponent } from '../../common/localSandbox';
import { GzProxyConfigurationWebComponent } from '../../common/proxyConfiguration';
import { GzContentPrefilteringWebComponent } from '../../common/contentPrefiltering';
import {
  convertDbValueToFormValue,
  convertFormValueToDbValue,
} from '../../common/contentPrefiltering/component/complex-selection-group-config';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { GzDetonationProfileWebComponent } from '../../common/detonationProfile';
import { getTitleForLocalSandbox } from '../../common/localSandbox/utils';
import { combineLatest, skip } from 'rxjs';
import { GzFileSizesWebComponent } from '../../common/fileSizes';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_SANDBOX_ICAP_SENSOR)
export class GzSandboxIcapSensorWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  IcapModuleFormValue,
  SandboxIcapSensorSearchableKeywords,
  ManualSubmissionApiClients
> {
  /**
   * Path for Sandbox ICAP Sensor section.
   */
  protected path = {
    moduleId: ModuleId.SANDBOX,
    sectionId: SectionId.ICAP_SENSOR,
  };

  /**
   * Searchable keywords for Sandbox ICAP Sensor section.
   */
  protected searchableKeywords = getSandboxIcapSensorSearchableKeywords();

  @query('gz-policy-panel-sandbox-local-instance')
  private localSandboxInstance: GzLocalSandboxInstanceWebComponent;

  @query('gz-policy-panel-sandbox-proxy-configuration')
  private proxyConfiguration: GzProxyConfigurationWebComponent;

  @query('#automatic-submission-form-field')
  private automaticSubmissionFormField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#automatic-submission-info-text')
  private automaticSubmissionInfoText: GzTextWebComponent;

  @query('#automatic-submission-content')
  private automaticSubmissionContent: HTMLElement;

  @query('gz-policy-panel-sandbox-content-prefiltering')
  private contentPrefiltering: GzContentPrefilteringWebComponent;

  @query('#file-sizes-form-field')
  private fileSizesFormField: GzFormFieldWebComponent<GzFileSizesWebComponent>;

  @query('#submit-files-form-field')
  private submitFilesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('gz-policy-panel-sandbox-detonation-profile')
  private detonationProfileComponent: GzDetonationProfileWebComponent;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="automatic-submission-form-field" status-field>
      <gz-toggle
        class="content"
        gz-form-input
        searchable-label="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.AutomaticSubmission].label}"
        @toggleChange="${e => {
          toggleDisabled(this.automaticSubmissionInfoText, !e.detail.checked);
          toggleDisabled(this.automaticSubmissionContent, !e.detail.checked);
          this.localSandboxInstance.disabled = !e.detail.checked;
        }}"
      >
        <span>${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.AutomaticSubmission].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="automatic-submission-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Enable Security Servers used for scanning storage devices to act as an ICAP sensor and submit samples to Sandbox Analyzer.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="automatic-submission-content" class="mt-2">
        <gz-info-box severity="informative">
          ${translateFn(
            'To configure a Security Server to scan storage devices, go to the Storage Protection > ICAP section.'
          )}
        </gz-info-box>

        ${this.renderContentPrefiltering()} ${this.renderFileSizes()} ${this.renderSubmitFiles()}

        <div>
          <gz-title
            searchable-label="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.ConnectionSettings].label}"
            class="module-subtitle"
          >
            ${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.ConnectionSettings].translatedLabel}
          </gz-title>

          ${this.renderSandboxAnalyzerInstanceFields()} ${this.renderProxyConfiguration()}
        </div>

        ${this.renderDetonationProfile()}
      </div>
    `;
  }

  private renderProxyConfiguration(): TemplateResult {
    return html` <gz-policy-panel-sandbox-proxy-configuration
      .proxyConfigurationLabel="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.ProxyConfiguration]}"
    ></gz-policy-panel-sandbox-proxy-configuration>`;
  }

  private renderContentPrefiltering(): TemplateResult {
    return html` <gz-policy-panel-sandbox-content-prefiltering
      .contentPrefilteringLabel="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.ContentPrefiltering]}"
      .exceptionsLabel="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.Exclusions]}"
    ></gz-policy-panel-sandbox-content-prefiltering>`;
  }

  private renderFileSizes(): TemplateResult {
    return html`
      <gz-form-field id="file-sizes-form-field">
        <gz-policy-panel-sandbox-file-sizes
          gz-form-input
          .sizeFilterLabel="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.DoNotSubmit]}"
        ></gz-policy-panel-sandbox-file-sizes>
      </gz-form-field>
    `;
  }

  private renderSubmitFiles(): TemplateResult {
    return html`
      <gz-form-field id="submit-files-form-field">
        <gz-checkbox
          gz-form-input
          searchable-label="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.SubmitFiles].label}}"
          label="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.SubmitFiles].translatedLabel}"
        ></gz-checkbox>
      </gz-form-field>
    `;
  }

  private renderDetonationProfile(): TemplateResult {
    return html` <gz-policy-panel-sandbox-detonation-profile
      .detonationProfileLabel="${this.searchableKeywords[SandboxIcapSensorSearchableKeywords.DetonationProfile]}"
    ></gz-policy-panel-sandbox-detonation-profile>`;
  }

  private renderSandboxAnalyzerInstanceFields(): TemplateResult {
    return html`
      <gz-policy-panel-sandbox-local-instance
        .hasInfoText="${true}"
        class="mt-2"
      ></gz-policy-panel-sandbox-local-instance>
    `;
  }

  /**
   * Set clients.
   */
  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'ManualSubmission'
    ) as ManualSubmissionApiClients;

    if (this.localSandboxInstance) {
      this.localSandboxInstance.getLocalSandboxes = this.clients.getLocalSandboxes;
    }
  }

  /**
   * Subscribe to validity event received from form group and port section.
   */
  protected subscribeToValidityEvent() {
    // combine into a single observable (skip first value, because it is always true)
    const formGroupValidSubscription = combineLatest([this.formGroup.valid$, this.fileSizesFormField.Input.valid$])
      .pipe(skip(1))
      .subscribe({
        next: ([formGroupValid, fileSizesValid]) => {
          // wait for all values to be defined
          if (fileSizesValid === undefined) {
            return;
          }

          this.changeValidityIfNecessary(formGroupValid && fileSizesValid);
        },
      });

    this.subscriptions.push(formGroupValidSubscription);
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.ghostr
   */
  protected getValueFromManager(): IcapModuleFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [SectionId.ICAP_SENSOR]: ICAP_DEFAULT_VALUE,
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [SectionId.ICAP_SENSOR]: PolicyPanelManager.getInstance().getPolicy('uiSettings.icapSensor'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        icapSensor: {
          enabled: this.automaticSubmissionFormField,
          sandboxAnalyzerSettings: {
            localSandbox: this.localSandboxInstance?.formGroup || new FormGroup({}),
            detonationProfile: this.detonationProfileComponent?.formGroup || new FormGroup({}),
          },
          proxy: this.proxyConfiguration.formGroup,
          contentPrefiltering: this.contentPrefiltering.formGroup,
          sizeFilter: this.fileSizesFormField,
          submitFilesMarkedAsMalware: this.submitFilesFormField,
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    if (this.localSandboxInstance) {
      this.localSandboxInstance.selectedOption = {
        title: getTitleForLocalSandbox(
          this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.hostname,
          this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.ip
        ),
        value: this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.endpointId || '',
      };
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        icapSensor: {
          enabled: this.valueT.uiSettings.icapSensor.enabled,
          sandboxAnalyzerSettings: {
            localSandbox: {
              endpointId: this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.endpointId,
            },
            detonationProfile: {
              value: this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.detonationProfile,
            },
          },
          proxy: this.valueT.uiSettings.icapSensor.proxy,
          contentPrefiltering: {
            categories: convertDbValueToFormValue(this.valueT.uiSettings.icapSensor.contentPrefiltering.categories),
            excludeExtensions: this.valueT.uiSettings.icapSensor.contentPrefiltering.excludeExtensions,
          },
          sizeFilter: this.valueT.uiSettings.icapSensor.contentPrefiltering.sizeFilter,
          submitFilesMarkedAsMalware: this.valueT.uiSettings.icapSensor.contentPrefiltering.submitFilesMarkedAsMalware,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup || !this.valueT) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // save local sandbox
    const localSandboxFormValue = this.localSandboxInstance?.value;
    const localSandboxToSave = {
      ip: localSandboxFormValue
        ? localSandboxFormValue.ip
        : this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.ip,
      hostname: localSandboxFormValue
        ? localSandboxFormValue.name
        : this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.hostname,
      fqdn: localSandboxFormValue
        ? localSandboxFormValue.fqdn
        : this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.fqdn,
      endpointId: localSandboxFormValue
        ? localSandboxFormValue.endpointId
        : this.valueT.uiSettings.icapSensor.sandboxAnalyzerSettings.endpointId,
    };

    _.unset(value, ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'localSandbox']);
    _.set(value, ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'endpointId'], localSandboxToSave.endpointId);
    _.set(
      value,
      ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'hostname'],
      localSandboxToSave.hostname ?? ''
    );
    _.set(value, ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'fqdn'], localSandboxToSave.fqdn ?? '');
    _.set(value, ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'ip'], localSandboxToSave.ip ?? '');

    // save detonation profile
    const detonationProfile = _.get(
      value,
      ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'detonationProfile', 'value'],
      null
    );
    if (detonationProfile !== null) {
      _.set(value, ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'detonationProfile'], detonationProfile);
    } else {
      _.unset(value, ['uiSettings', 'icapSensor', 'sandboxAnalyzerSettings', 'detonationProfile']);
    }

    // save categories
    const categories = _.get(value, ['uiSettings', 'icapSensor', 'contentPrefiltering', 'categories']);
    _.set(
      value,
      ['uiSettings', 'icapSensor', 'contentPrefiltering', 'categories'],
      convertFormValueToDbValue(categories)
    );

    // save file sizes
    const fileSizes = _.get(value, ['uiSettings', 'icapSensor', 'sizeFilter']);
    _.unset(value, ['uiSettings', 'icapSensor', 'sizeFilter']);
    _.set(value, ['uiSettings', 'icapSensor', 'contentPrefiltering', 'sizeFilter'], fileSizes);

    // save submit files marked as malware
    const submitFilesMarkedAsMalware = _.get(value, ['uiSettings', 'icapSensor', 'submitFilesMarkedAsMalware']);
    _.unset(value, ['uiSettings', 'icapSensor', 'submitFilesMarkedAsMalware']);
    _.set(
      value,
      ['uiSettings', 'icapSensor', 'contentPrefiltering', 'submitFilesMarkedAsMalware'],
      submitFilesMarkedAsMalware
    );

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'icapSensor'], {});
    }

    return value;
  }

  /**
   * Override reset method, because we need to reset also chips component.
   */
  public reset() {
    super.reset();
    this.contentPrefiltering.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_SANDBOX_ICAP_SENSOR: GzSandboxIcapSensorWebComponent;
  }
}
